import { useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import useAPI, { UseAPIOptions, useAPIMutation } from "@/services/api/hook";
import {
  nextPageNumber,
  useInfiniteFilterQuery
} from "@/services/api/useInfiniteFilterQuery";
import {
  invalidateReportsList,
  SAVED_REPORTS_FILTER_KEY,
  SAVED_REPORTS_UNSEEN_COUNT_KEY
} from "@/entities/savedReports/queryKeys";
import {
  SCHEDULER_ITEM_KEY,
  SCHEDULER_KEYS,
  SCHEDULER_LIST_KEY
} from "@/entities/reportingScheduler/queryKeys";
import { ReportingScheduler } from "@/entities/reportingScheduler/types";
import {
  ReportRunDetail,
  ReportsUnseenCount,
  SavedReport,
  SavedReportsPage,
  RunNowAccepted
} from "@/entities/savedReports/types";

const SAVED_REPORTS_RUN_KEY = "reports/saved/run";
const SAVED_REPORTS_RUN_ITEM_KEY = "reports/saved/run/item";
const SAVED_REPORTS_SEEN_KEY = "reports/saved/seen";

const UNSEEN_COUNT_REQUEST: UseAPIOptions = { method: "GET" };

const DEFAULT_PAGE_SIZE = 20;

interface SavedReportsInfiniteOptions {
  scheduledOnly?: boolean;
  pageSize?: number;
  enabled?: boolean;
}

/**
 * The reports list, paged. The filter travels as a POST body while the paging
 * travels on the query string, which is the shape `useInfiniteFilterQuery`
 * exists for.
 */
export const useSavedReportsInfinite = ({
  scheduledOnly = false,
  pageSize = DEFAULT_PAGE_SIZE,
  enabled = true
}: SavedReportsInfiniteOptions = {}) => {
  const requestOptions: UseAPIOptions = useMemo(
    () => ({
      method: "POST",
      queryParams: { size: pageSize.toString() },
      data: { scheduledOnly }
    }),
    [pageSize, scheduledOnly]
  );

  const { pages, ...query } = useInfiniteFilterQuery<SavedReportsPage>({
    routeKey: SAVED_REPORTS_FILTER_KEY,
    requestOptions,
    enabled,
    getNextPageParam: nextPageNumber
  });

  const reports = useMemo(
    () => pages?.flatMap((page) => page.reports) ?? [],
    [pages]
  );

  return {
    reports,
    totalReports: pages?.[0]?.total ?? 0,
    isLoading: query.isLoading,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: query.fetchNextPage,
    error: query.error
  };
};

const invalidateSavedReports = (
  queryClient: ReturnType<typeof useQueryClient>
) => {
  // Saving or removing a report moves the Reports tab badge.
  invalidateReportsList(queryClient);
};

type CachedReportsPages =
  | { pages: SavedReportsPage[]; pageParams: unknown[] }
  | undefined;

const updateCachedReports = (
  queryClient: ReturnType<typeof useQueryClient>,
  updateReports: (reports: SavedReport[]) => SavedReport[]
) =>
  queryClient.setQueriesData<CachedReportsPages>(
    { queryKey: [SAVED_REPORTS_FILTER_KEY] },
    (cached) =>
      cached && {
        ...cached,
        pages: cached.pages.map((page) => ({
          ...page,
          reports: updateReports(page.reports)
        }))
      }
  );

/** A report from whichever reports list this tab already loaded, if any did. */
export const findCachedReport = (
  queryClient: ReturnType<typeof useQueryClient>,
  reportId: string
): SavedReport | undefined =>
  queryClient
    .getQueriesData<CachedReportsPages>({ queryKey: [SAVED_REPORTS_FILTER_KEY] })
    .flatMap(([, cached]) => cached?.pages ?? [])
    .flatMap((page) => page.reports)
    .find((report) => report.id === reportId);

const invalidateScheduler = (queryClient: ReturnType<typeof useQueryClient>) =>
  SCHEDULER_KEYS.forEach((key) =>
    queryClient.invalidateQueries({ queryKey: [key] })
  );

/**
 * Saves a chat answer as a report, with no cadence.
 *
 * The same POST the schedule modal creates with, minus `scheduleConfig` and
 * `channels`: the server takes the two together or not at all, and both absent
 * is what a saved-but-unscheduled report is.
 */
export const useSaveReport = () => {
  const queryClient = useQueryClient();
  return useAPIMutation<
    { data: { messageId: string; name?: string } },
    // A save carries no cadence, so only the new row's id is relied on.
    Pick<ReportingScheduler, "id">
  >(SCHEDULER_LIST_KEY, { method: "POST" }, () => {
    invalidateSavedReports(queryClient);
    invalidateScheduler(queryClient);
  });
};

/** A string field of a 409's `detail` object, when the refusal carries one. */
const conflictDetailString = (error: unknown, key: string): string | null => {
  if (!axios.isAxiosError(error) || error.response?.status !== 409) return null;
  const detail = (error.response.data as { detail?: unknown } | undefined)
    ?.detail;
  if (!detail || typeof detail !== "object") return null;
  const value = (detail as Record<string, unknown>)[key];
  return typeof value === "string" ? value : null;
};

/**
 * The report a refused save names, when the answer was already saved.
 *
 * The 409 carries the existing row's id as a field beside the message, so it is
 * read from there rather than from the sentence.
 */
export const alreadySavedReportId = (error: unknown): string | null =>
  conflictDetailString(error, "reportId");

/**
 * Dispatches a replay of the report's origin. Nothing to poll here: the result
 * arrives as a `reportRun` user event over the websocket.
 */
export const useRunReportNow = (onSuccess?: (data: RunNowAccepted) => void) =>
  useAPIMutation<{ pathParams: { reportId: string } }, RunNowAccepted>(
    SAVED_REPORTS_RUN_KEY,
    { method: "POST" },
    onSuccess
  );

/**
 * The run already in flight that a refused run names. The 409 carries its id
 * beside the message, so the caller can follow that run instead of starting a
 * second one.
 */
export const runIdFromConflict = (error: unknown): string | null =>
  conflictDetailString(error, "runId");

/**
 * Reads one run back. Used only when the `reportRun` event for it may have been
 * missed: after the socket reconnects, and when a run has gone quiet for too
 * long.
 */
export const useFetchReportRun = () =>
  useAPIMutation<
    { pathParams: { reportId: string; runId: string } },
    ReportRunDetail
  >(SAVED_REPORTS_RUN_ITEM_KEY, { method: "GET" });

/**
 * How many reports were saved or scheduled since the user last looked at the
 * Reports tab. Counted on the server, so every device shows the same number.
 */
export const useReportsUnseenCount = (enabled: boolean) => {
  const query = useAPI(SAVED_REPORTS_UNSEEN_COUNT_KEY, UNSEEN_COUNT_REQUEST, {
    enabled,
    refetchOnWindowFocus: true
  });
  const data = query.data as ReportsUnseenCount | undefined;
  return { unseenCount: data?.count ?? 0, refetch: query.refetch };
};

/**
 * Tells the server the user has now seen every report, and zeroes the badge
 * before it answers. A failure puts the old number back.
 */
export const useMarkReportsSeen = () => {
  const queryClient = useQueryClient();
  return useAPIMutation<
    Record<string, never>,
    void,
    { previousCounts: [readonly unknown[], ReportsUnseenCount | undefined][] }
  >(SAVED_REPORTS_SEEN_KEY, { method: "PUT" }, undefined, undefined, {
    onMutate: async () => {
      const filters = { queryKey: [SAVED_REPORTS_UNSEEN_COUNT_KEY] };
      await queryClient.cancelQueries(filters);
      const previousCounts =
        queryClient.getQueriesData<ReportsUnseenCount>(filters);
      queryClient.setQueriesData<ReportsUnseenCount>(filters, (cached) =>
        cached ? { ...cached, count: 0 } : cached
      );
      return { previousCounts };
    },
    onError: (_error, _variables, context) => {
      context?.previousCounts.forEach(([queryKey, cached]) =>
        queryClient.setQueryData<ReportsUnseenCount | undefined>(
          queryKey,
          cached
        )
      );
    }
  });
};

/**
 * Renames the report. This is the scheduler PUT, which is a merge: sending the
 * name alone leaves the cadence, the channels and the message link exactly as
 * they are, which is why a rename does not need to resend the schedule it is
 * not changing.
 *
 * Patches the cached row, then refetches. The scheduler keys go too — saving
 * and scheduling are one row, so the Manage-schedules table reads the same name.
 */
export const useRenameReport = () => {
  const queryClient = useQueryClient();
  return useAPIMutation<
    { pathParams: { id: string }; data: { name: string } },
    SavedReport
  >(SCHEDULER_ITEM_KEY, { method: "PUT" }, (response, variables) => {
    const renamedId = variables.pathParams.id;
    updateCachedReports(queryClient, (reports) =>
      reports.map((report) =>
        report.id === renamedId ? { ...report, name: response.name } : report
      )
    );
    invalidateSavedReports(queryClient);
    invalidateScheduler(queryClient);
  });
};

/**
 * Drops the report's cadence and leaves the report itself saved.
 *
 * This is the same merge PUT the rename uses, with an explicit null
 * `scheduleConfig`: absent means leave the cadence alone, null means clear it.
 * Saving and scheduling are one row, so `DELETE /scheduler/{id}` would take the
 * saved report away with its schedule — which is the delete below, not this.
 */
export const useUnscheduleReport = () => {
  const queryClient = useQueryClient();
  return useAPIMutation<
    { pathParams: { id: string }; data: { scheduleConfig: null } },
    SavedReport
  >(SCHEDULER_ITEM_KEY, { method: "PUT" }, () => {
    invalidateSavedReports(queryClient);
    invalidateScheduler(queryClient);
  });
};

/**
 * Removes the report. Saving and scheduling are one row, so this removes its
 * schedule with it — there is no separate unschedule to do first. The
 * deliveries it already produced stay in chat.
 */
export const useDeleteReport = () => {
  const queryClient = useQueryClient();
  return useAPIMutation<{ pathParams: { id: string } }, void>(
    SCHEDULER_ITEM_KEY,
    { method: "DELETE" },
    (_response, variables) => {
      const deletedId = variables.pathParams.id;
      updateCachedReports(queryClient, (reports) =>
        reports.filter((report) => report.id !== deletedId)
      );
      invalidateSavedReports(queryClient);
      invalidateScheduler(queryClient);
    }
  );
};
